import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, Mail, ArrowRight, Network, Monitor, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserFromStorage } = useAuth();

  const loginMutation = useMutation({
    mutationFn: (credentials) => api.post('/auth/login', credentials),
    onSuccess: async (response) => {
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      // Update the auth context state
      setUserFromStorage();
      toast.success('Welcome back!');

      // Role-based navigation
      if (user.role === 'cashier') {
        navigate('/orders/create');
      } else if (user.role === 'manager') {
        navigate('/out-of-stock-sales');
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.detail || data?.error || 'Login failed. Please try again.';
      toast.error(message);
      setIsLoading(false);
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    loginMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main className="login-page">
      <div className="login-background" aria-hidden="true"><span className="login-outline-square one" /><span className="login-outline-square two" /><span className="login-outline-circle" /><Network className="login-network" strokeWidth={.8} /><Monitor className="login-monitor" strokeWidth={.8} /></div>
      <div className="login-card">
        <aside className="login-brand-panel">
          <div className="login-brand-content"><h1>Welcome back</h1><p>Bring your shops, sales, and inventory together.<br />Everything you need to keep your business moving.</p></div>
          <span className="login-panel-circle top" aria-hidden="true" /><span className="login-panel-circle bottom" aria-hidden="true" />
        </aside>
        <section className="login-form-panel">
          <header className="login-card-heading">
            <div className="login-wordmark" aria-label="Lika Perfume"><span>Lika</span><span className="login-logo-badge"><span>PERFUME</span></span></div>
            <h2>Sign in</h2><p>Enter your username or email and password to continue.</p>
          </header>
          <form onSubmit={handleSubmit} className="login-form" aria-label="Sign in">
            <div className="login-field"><label htmlFor="username">Username or email<span aria-hidden="true">*</span></label>
              <div className="login-input-wrap"><Mail size={16} aria-hidden="true" /><input id="username" name="username" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} required value={formData.username} onChange={handleInputChange} placeholder="Enter your username or email" disabled={isLoading} /></div>
            </div>
            <div className="login-field"><label htmlFor="password">Password<span aria-hidden="true">*</span></label>
              <div className="login-input-wrap"><Lock size={16} aria-hidden="true" /><input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={formData.password} onChange={handleInputChange} placeholder="Enter your password" disabled={isLoading} /><button type="button" className="login-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
            </div>
            <div className="login-help"><button type="button" onClick={() => toast('Contact your administrator to reset your password.')}>Forgot password?</button></div>
            <button type="submit" disabled={isLoading} className="login-submit">{isLoading ? <><Loader2 size={18} className="animate-spin" /> Signing in…</> : <>Sign in <ArrowRight size={16} /></>}</button>
          </form>
          <footer className="login-form-footer">No account yet? Ask your administrator.</footer>
        </section>
      </div>
    </main>
  );
};

export default Login;
